import { useState } from "react";
import { invoke } from "../lib/bridge";
import { WaitingForApproval } from "./WaitingForApproval";
import { Alert } from "./ui/Alert";
import type { FamilyInfo, JoinResult } from "../lib/onboardingTypes";

export interface InviteData {
  code: string;
  n: string;
  deviceLink: boolean;
}

interface JoinFamilyProps {
  invite: InviteData;
  onJoined: (info: FamilyInfo) => void;
  onCancel: () => void;
}

export function JoinFamily({ invite, onJoined, onCancel }: JoinFamilyProps) {
  const [memberName, setMemberName] = useState("");
  const [joining, setJoining] = useState(false);
  const [pendingJoin, setPendingJoin] = useState<JoinResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (name: string) => {
    setJoining(true);
    setError(null);
    try {
      const result = await invoke<JoinResult>("join_family", {
        code: invite.code,
        memberName: name,
      });
      setPendingJoin(result);
    } catch (e) {
      console.error("Failed to join family:", e);
      setError("Could not join the family. Please check your invite code and try again.");
      setJoining(false);
    }
  };

  if (pendingJoin) {
    return (
      <WaitingForApproval
        joinRequestId={pendingJoin.joinRequestId}
        familyName={pendingJoin.familyName}
        deviceLink={invite.deviceLink}
        onApproved={onJoined}
        onRejected={onCancel}
      />
    );
  }

  if (invite.deviceLink) {
    return (
      <main className="container">
        <h1 className="family-name">Link this device</h1>
        <p style={{ textAlign: "center", color: "var(--color-hint)", marginBottom: "24px" }}>
          Add this device to your <strong>{invite.n}</strong> account. Your existing data will sync here.
        </p>
        {error && <Alert>{error}</Alert>}
        <button
          onClick={() => submit("")}
          disabled={joining}
          className="btn-primary btn-full"
        >
          {joining ? "Linking..." : "Link this device"}
        </button>
        <button
          onClick={onCancel}
          className="btn-secondary btn-full"
          style={{ marginTop: "12px" }}
        >
          Cancel
        </button>
      </main>
    );
  }

  return (
    <main className="container">
      <h1 className="family-name">Join Family</h1>
      <p style={{ textAlign: "center", color: "var(--color-hint)", marginBottom: "24px" }}>
        You've been invited to join <strong>{invite.n}</strong>
      </p>
      <div style={{ marginBottom: "24px" }}>
        <label className="field-label" htmlFor="join-member-name">What's your name?</label>
        <input
          id="join-member-name"
          value={memberName}
          onChange={(e) => setMemberName(e.target.value)}
          placeholder="Your name"
          className="field-input"
        />
      </div>
      {error && <Alert>{error}</Alert>}
      <button
        onClick={() => memberName.trim() && submit(memberName.trim())}
        disabled={joining || !memberName.trim()}
        className="btn-primary btn-full"
      >
        {joining ? "Joining..." : `Join ${invite.n}`}
      </button>
      <button
        onClick={onCancel}
        className="btn-secondary btn-full"
        style={{ marginTop: "12px" }}
      >
        Cancel
      </button>
    </main>
  );
}
